import React from 'react'
import { Typography, Link, ListItem, List } from '@leshen/gatsby-theme-leshen'
import Table from '../components/Table'

export const indexFaq = () => [
  {
    title: 'How do I check if Hughesnet is available in my area?',
    content: (
      <div>
        <Typography>
          To check Hughesnet availability in your area, enter your ZIP code or
          select your state above.
        </Typography>
      </div>
    ),
  },
  {
    title: 'What types of internet plans does Hughesnet offer, and are they available in all areas?',
    content: (
      <div>
        <Typography>
          Hughesnet internet plans start at speeds up to 50 Mbps**, with
          Priority Data amounts of 50 GB per month and unlimited Standard Data.
        </Typography>
        <Typography>
          In select areas, new Fusion plans with lower latency are also
          available. These plans run up to 100 Mbps and enable more streaming
          and gaming with less lag.
        </Typography>
        <Typography>
          With all Hughesnet packages, after using your Priority Data,
          you&apos;ll revert to unlimited Standard Data at lower speeds.
          You&apos;re always welcome to top up your speeds with Priority Data
          Tokens.
        </Typography>
      </div>
    ),
  },
  {
    title: 'Are there any upcoming expansions of Hughesnet coverage in my city?',
    content: (
      <div>
        <Typography>
          Hughesnet internet availability spans suburban and rural areas of the
          contiguous USA, and there currently aren&apos;t plans to add new
          territory.
        </Typography>
        <Typography>
          However, Hughesnet has recently introduced a new internet technology
          that will eventually expand to more customers in current service
          areas. This new service, called Fusion, combines the power of
          satellite and land-based wireless service to reduce latency, making
          rural internet more efficient and responsive than ever.
        </Typography>
      </div>
    ),
  },
  {
    title: 'What is satellite internet service?',
    content: (
      <div>
        <Typography>
          Satellite internet service is a way of connecting households to the
          worldwide web using satellites orbiting Earth. Because satellites
          don&apos;t require pricey, extensive underground networks,
          they&apos;re not limited by geography and can reach rural homes as
          easily as urban ones.
        </Typography>
        <Typography>
          When you need internet in rural areas, you can count on Hughesnet
          availability—and the new unlimited data plans, faster speeds, and
          low-latency options in select areas.
        </Typography>
      </div>
    ),
  },
  {
    title: 'What is required to get Hughesnet internet?',
    content: (
      <div>
        <Typography>
          The only requirement to get Hughesnet internet is to live within the
          Hughesnet service area—which spans 48 states.
        </Typography>
      </div>
    ),
  },
  {
    title: 'What are my plan options and which plan should I choose?',
    content: (
      <div>
        <Typography>
          Hughesnet availability includes three unlimited data plans, two of
          which are Fusion plans available only in select areas:
        </Typography>
        <List type="unordered">
          <ListItem>
            Select — speeds up to 50 Mbps<sup>**</sup> with 100 GB Priority Data
          </ListItem>
          <ListItem>
            Elite — speeds up to 50 or up to 100 Mbps<sup>**</sup> with 200 GB
            Priority Data
          </ListItem>
          <ListItem>
            Fusion — speeds up to 100 Mbps<sup>**</sup> with 200 GB Priority
            Data
          </ListItem>
        </List>
        <Typography>
          To choose your Hughesnet plan, select which Priority Data amount will
          last the longest on your billing cycle. If you like to video chat,
          stream, or game online as much as possible, choose a plan with more
          Priority Data.
        </Typography>
      </div>
    ),
  },
]

export const cityFaq = ({ City, State, rotatedNumber }) => {
  const tableData = {
    headings: ['Plan Name', 'Speed (Mbps)', 'Priority Data'],
    rows: [
      ['Select', '50', '100 GB'],
      ['Elite', '50 or 100', '200 GB'],
    ],
    sticky: true,
  }

  return [
    {
      title: `Is Hughesnet available in ${City}?`,
      content: (
        <div>
          <Typography>
            Yes! Hughesnet internet in {City}, {State}, runs at plan speeds up
            to 100 Mbps<sup>**</sup> and Priority Data amounts up to 200 GB.
            View the complete list of{' '}
            <Link to="/bundles">plans & pricing here</Link> and start imagining
            your life with the new Hughesnet today.
          </Typography>
        </div>
      ),
    },
    {
      title: `What are the Hughesnet plans in ${City}?`,
      content: (
        <Typography>
          Hughesnet in {City} includes the following packages:
          <Table json={tableData} />
        </Typography>
      ),
    },
    {
      title: `How does Hughesnet compare to other internet providers in ${City}?`,
      content: (
        <div>
          <Typography>
            Internet providers&apos; availability, speeds, and pricing can vary
            over time, so it&apos;s best to go to the source when comparing
            internet services in {City}.
          </Typography>
          <Typography>
            In many rural areas, Hughesnet provides the most reliably available
            internet service. That&apos;s because while one Hughesnet satellite
            can easily reach two locations 100 miles apart, other providers need
            two or more towers placed precisely to reach both spots. Call{' '}
            <Link to={`tel:${rotatedNumber}`}>{rotatedNumber} </Link> today for
            Hughesnet availability in {City} you can count on today and
            tomorrow.
          </Typography>
        </div>
      ),
    },
    {
      title: 'Is Hughesnet available in my city?',
      content: (
        <div>
          <Typography>
            Hughesnet is available in most cities across the contiguous United
            States. Call{' '}
            <Link to={`tel:${rotatedNumber}`}>{rotatedNumber}</Link> to check
            availability in your city now.
          </Typography>
        </div>
      ),
    },
    {
      title: 'What Hughesnet plans are available in my area?',
      content: (
        <div>
          <Typography>
            <Link to="/">View Hughesnet plan availability here ›</Link>
          </Typography>
        </div>
      ),
    },
    {
      title: `How reliable is Hughesnet in ${City}?`,
      content: (
        <div>
          <Typography>
            Because satellite networks are located in space, satellite service
            comes with slightly higher latency, or lag, than land-based
            services. That said, Hughesnet is currently rolling out new
            low-latency Fusion plans that significantly reduce that lag time for
            a more responsive experience. View {City} plans here to check if
            Fusion service is available in your area today.
          </Typography>
        </div>
      ),
    },
    {
      title: 'What factors affect Hughesnet availability in different cities?',
      content: (
        <div>
          <Typography>
            Hughesnet internet availability spans the entire contiguous United
            States, and very few places with residential zoning are out of the
            Hughesnet footprint. Exceptions may include fully off-the-grid
            dwellings. Call{' '}
            <Link to={`tel:${rotatedNumber}`}>{rotatedNumber}</Link> anytime
            with questions about Hughesnet availability in your area.
          </Typography>
        </div>
      ),
    },
    {
      title: 'Are there any upcoming expansions of Hughesnet coverage in my city?',
      content: (
        <div>
          <Typography>
            Hughesnet is already available in most areas in the contiguous
            United States, but you may soon be eligible for Hughesnet&apos;s
            low-latency Fusion plans. For the latest Hughesnet availability in
            {City}, call{' '}
            <Link to={`tel:${rotatedNumber}`}>{rotatedNumber}</Link> now and
            enter your ZIP code.
          </Typography>
        </div>
      ),
    },
  ]
}

export const stateFaq = ({ State, rotatedNumber }) => [
  {
    title: 'Can you get Hughesnet anywhere?',
    content: (
      <div>
        <Typography>
          Hughesnet is available in the US mainland-even in the most rural
          residential areas. Call{' '}
          <Link to={`tel:${rotatedNumber}`}>{rotatedNumber}</Link> to check the
          availability of Hughesnet&apos;s new low-latency Fusion plans.
        </Typography>
      </div>
    ),
  },
  {
    title: 'Is Hughesnet fast enough for Netflix streaming?',
    content: (
      <div>
        <Typography>
          Yes! Hughesnet speeds start at up to 50 Mbps<sup>**</sup>, which is
          enough for streaming Netflix and other services. That said, streaming
          can use your Priority Data quickly, especially if you stream often,
          for long periods, or at maximum resolutions.
        </Typography>
        <Typography>
          If streaming is a priority for you, choose a plan with plenty of
          Priority Data so you can count on full speeds during your favorite
          pastime. You&apos;re always welcome to add Priority Data Tokens until
          your billing cycle restarts.
        </Typography>
      </div>
    ),
  },
  {
    title: 'What are the advantages of Hughesnet and satellite internet?',
    content: (
      <div>
        <Typography>
          Satellite internet&apos;s main advantage is wide availability. Most
          internet providers won&apos;t build out their underground networks to
          sparsely populated areas because of the cost. Satellite providers, on
          the other hand, send internet signal over radio waves to and from
          space-which work regardless of your distance from an urban center.
        </Typography>
        <Typography>
          For over 20 years, Hughesnet has been the leading high-speed satellite
          internet service in rural America. Today Hughesnet provides home
          internet service with unlimited Standard Data, faster speeds up to 100
          Mbps<sup>**</sup>, and new lower latency in select areas for a more
          responsive internet experience.
        </Typography>
      </div>
    ),
  },
  {
    title: `Is Hughesnet available in ${State}?`,
    content: (
      <div>
        <Typography>
          Yes! Hughesnet is available in {State}, and select areas are eligible
          for new low-latency Fusion plans. Call{' '}
          <Link to={`tel:${rotatedNumber}`}>{rotatedNumber}</Link> to check
          availability now.
        </Typography>
      </div>
    ),
  },
  {
    title: `Does ${State} offer Hughesnet service?`,
    content: (
      <div>
        <Typography>
          Yes. Hughesnet offers service in {State}, with select areas qualifying
          for new low-latency Fusion plans. Select your city to view{' '}
          <Link to="/bundles">plans & pricing</Link> near you.{' '}
          <Link to="#select-city">Select City</Link>
        </Typography>
      </div>
    ),
  },
  {
    title: `Can I get Hughesnet near me in ${State}?`,
    content: (
      <div>
        <Typography>
          Yes! Hughesnet in {State} comes with unlimited Standard Data
          <sup>1</sup> and speeds up to 100 Mbps. Call{' '}
          <Link to={`tel:${rotatedNumber}`}>{rotatedNumber}</Link> to see if new
          low-latency Fusion plans are available near you.
        </Typography>
      </div>
    ),
  },
  {
    title: `Is Hughesnet satellite internet available in ${State}?`,
    content: (
      <div>
        <Typography>
          Yes! Hughesnet satellite internet availability spans {State}, with
          select areas eligible for new low-latency Fusion plans. Call{' '}
          <Link to={`tel:${rotatedNumber}`}>{rotatedNumber}</Link> for plans and
          pricing near you.
        </Typography>
      </div>
    ),
  },
]

export const bundlesFaq = () => [
  {
    title: 'Which Hughesnet plan is best?',
    content: (
      <div>
        <Typography>
          Choosing a Hughesnet plan depends upon your budget and your particular
          household needs. No one Hughesnet plan is best for everyone, but each
          available Hughesnet plan is best for someone.
        </Typography>
        <List>
          <ListItem>
            If cost is your number one factor, choose the Select plan. You’ll
            get 50 Mbps** download speeds, sufficient for most household surfing
            and streaming activities, upload speeds of up to 5 Mbps for video
            conferencing and gaming, and a low latency of 650 ms. Plans start at
            $59.99 per month.
          </ListItem>
          <ListItem>
            If speed is your number one factor, choose the Elite plan, upping
            your download speed to as high as 100 Mbps**.
          </ListItem>
          <ListItem>
            If latency is your number one factor, choose the Fusion plan, which
            offers the same speed as the Elite plan with a low latency of 100
            Mbps.
          </ListItem>
        </List>
      </div>
    ),
  },
  {
    title: 'Does DISH bundle with Hughesnet?',
    content: (
      <div>
        <Typography>
          You can purchase a DISH Network and Hughesnet satellite internet
          bundle. Choose the DISH TV package and Hughesnet internet plan you’d
          like and call phone number to place your order in one convenient
          transaction today.
        </Typography>
      </div>
    ),
  },
  {
    title: 'Does Hughesnet have an unlimited plan?',
    content: (
      <div>
        <Typography>
          All current Hughesnet satellite internet plans offer unlimited
          Standard Data1 including in your monthly price. Priority Data varies
          based on your package—with the Select package, you get 100 GB per
          month of Priority Data, while the Elite and Fusion plans offer 200 GB
          of Priority Data per month.
        </Typography>
      </div>
    ),
  },
  {
    title: 'What is included in a Hughesnet bundle?',
    content: (
      <div>
        <Typography>
          You can bundle Hughesnet satellite internet with Hughesnet Voice phone
          service, DISH Network satellite TV, or both. Prices and packages
          vary—call 1-888-888-8888 to find the combination of services at the
          right price for you.
        </Typography>
      </div>
    ),
  },
  {
    title: 'Can I customize my Hughesnet bundle to fit my needs?',
    content: (
      <div>
        <Typography>
          Yes, you can customize your Hughesnet bundle to fit your needs. If
          you’re bundling Hughesnet internet with DISH TV, you can choose the
          internet speed you need and the TV channel lineup you prefer. You can
          also add Hughesnet Voice telephone service for a full suite of
          entertainment and telco services.
        </Typography>
      </div>
    ),
  },
  {
    title: 'How does billing work with Hughesnet bundles?',
    content: (
      <div>
        <Typography>
          With a Hughesnet Internet and Hughesnet Voice bundle, you’ll receive
          one bill for both services monthly. When you bundle a Hughesnet plan
          with Dish TV, however, you’ll receive separate bills for your internet
          and/or voice service and your TV service.
        </Typography>
      </div>
    ),
  },
]

export const customerServiceFaq = ({ rotatedNumber }) => [
  {
    title: 'How/where can I purchase Hughesnet?',
    content: (
      <div>
        <Typography>
          You can order Hughesnet online, by phone or through a local authorized
          retailer.
        </Typography>
      </div>
    ),
  },
  {
    title: 'Whom do I contact to check the status of my order?',
    content: (
      <div>
        <Typography>
          Visit orderstatus.hughesnet.com or call Pre-installation Support at
          1-866-774-6580.
        </Typography>
      </div>
    ),
  },
  {
    title: 'What type of equipment is necessary for Hughesnet service?',
    content: (
      <div>
        <Typography>
          Hughesnet provides all necessary equipment, including the Wi-Fi Modem
          and satellite dish. Our professional technicians install all equipment
          to ensure optimal home internet performance.
        </Typography>
      </div>
    ),
  },
  {
    title: 'How do I talk to a human at Hughesnet?',
    content: (
      <div>
        <Typography>
          For tech support or questions about an existing account, call
          Hughesnet customer care at 1-833-922-5495. Experts are available 24/7.
          <br />
          If you don’t have a Hughesnet account yet, Call{' '}
          <Link to={`tel:${rotatedNumber}`}>{rotatedNumber}</Link> to speak to
          Hughesnet customer care about plans near you.
        </Typography>
      </div>
    ),
  },
  {
    title: 'How can I check my order status?',
    content: (
      <div>
        <Typography>
          Use the Hughesnet{' '}
          <Link to="https://dwayinstalls.hns.com/orderStatus/CustomerOrderStatus.jsp">
            Order Inquiry
          </Link>{' '}
          tool to check your order status.
          <br />
          For other questions about a recent order, call 1-833-922-5495.
        </Typography>
      </div>
    ),
  },
  {
    title: 'What Hughesnet customer care tools can I find online?',
    content: (
      <div>
        <Typography>
          Find these Hughesnet customer care resources and more at
          my.Hughesnet.com or in the myHughesnet mobile app.
        </Typography>
        <List type="unordered">
          <ListItem>
            <Link to="https://dwayinstalls.hns.com/orderStatus/CustomerOrderStatus.jsp">
              Order status
            </Link>{' '}
            request form{' '}
          </ListItem>
          <ListItem>
            <Link to="https://community.hughesnet.com/?utm_medium=acquisition_website">
              Hughesnet Community
            </Link>{' '}
            customer forum
          </ListItem>
          <ListItem>
            <Link to="https://my.hughesnet.com/en/support/faq?utm_medium=acquisition_website">
              Faq Center
            </Link>{' '}
            with support articles
          </ListItem>
          <Link to="https://livechat.hughesnet.com/assets/consumer/Captcha.html">
            Live chat{' '}
          </Link>
          for tech & billing help
        </List>
      </div>
    ),
  },
  {
    title: 'What should I do if I\'m experiencing issues with my Hughesnet service?',
    content: (
      <div>
        <Typography>First, try these troubleshooting tips.</Typography>
        <List type="unordered">
          <ListItem>Reset your modem</ListItem>
          <ListItem> Clear any debris from your satellite dish</ListItem>
          <ListItem>
            Check your data usage at my.Hughesnet.com or in the myHughesnet app
          </ListItem>
          <ListItem>Make sure your last bill is paid</ListItem>
          If you’re still experiencing issues, call the Hughesnet customer care
          number 1-833-922-5495 for help.
        </List>
      </div>
    ),
  },
]
